<template>
  <v-form ref="form" @submit.prevent :readonly="readonly">
    <v-row v-if="value">
      <v-col cols="12">
        <v-text-field
          label="Name"
          ref="name"
          :value="value.analysisName"
          @input="nameInput"
          hide-details="auto"
          :rules="combinedNameRules"
          autofocus
        ></v-text-field>
      </v-col>
      <v-col v-if="onlyDates" cols="12" md="4">
        <DateInput
          ref="startDate"
          :value="value.startDate"
          @input="startDateInput"
          label="Start date"
          prepend-icon="event"
          clearable
          hide-details="auto"
          persistent-hint
        ></DateInput>
      </v-col>
      <v-col v-else cols="12" md="4">
        <DateTimeInput
          ref="startDate"
          :value="value.startDate"
          :zone="timeZone"
          @input="startDateInput"
          label="Start date"
          prepend-icon="event"
          clearable
          hide-details="auto"
          persistent-hint
          :readonly="readonly"
        ></DateTimeInput>
      </v-col>
      <v-col cols="12" md="4">
        <DurationEdit
          ref="missionTime"
          label="Mission time"
          clearable
          :value="value.missionTime"
          @input="missionTimeInput"
          @click:clear="clearMissionTime"
          :rules="[localRules.missionTimeInRange]"
          defaultDurationType="years"
          :readonly="readonly"
        ></DurationEdit>
      </v-col>
      <v-col v-if="onlyDates" cols="12" md="4">
        <DateInput
          ref="endDate"
          :value="value.endDate"
          :displayFormat="ARA_DATE_FORMAT"
          @input="endDateInput"
          label="End date"
          prepend-icon="event"
          clearable
          @click:clear="clearEndDate"
          hide-details="auto"
          persistent-hint
          :rules="endDateRules"
        ></DateInput>
      </v-col>
      <v-col v-else cols="12" md="4">
        <DateTimeInput
          ref="endDate"
          :value="value.endDate"
          :displayFormat="ITUS_DATETIME_FORMAT"
          :zone="timeZone"
          @input="endDateInput"
          label="End date"
          prepend-icon="event"
          clearable
          hide-details="auto"
          persistent-hint
          :rules="endDateRules"
          :hint="endDateHint"
          :readonly="readonly"
        ></DateTimeInput>
      </v-col>
      <v-col cols="12">
        <v-textarea
          rows="1"
          auto-grow
          hide-details="auto"
          label="Description"
          :value="value.description"
          :rules="[standardRules.maxLength.bind(null, 500)]"
          @input="descriptionInput"
        ></v-textarea>
      </v-col>
    </v-row>
    <WarningDialog
      ref="warningDialog"
      :allowSuppression="true"
      title="Invalidates what-if"
      text="Are you sure you want to make this change and invalidate existing what-if scenarios?"
    ></WarningDialog>
  </v-form>
</template>

<script>
import { DateTime, Duration } from 'luxon'
import { DurationEdit, Convert, Rules, Time, DateInput, DateTimeInput, WarningDialog } from '@itus/ui.common'
import { RiskAnalysisModel } from './risk-analysis-model'
import { mapState } from 'vuex'

export default {
  name: 'RiskAnalysisForm',
  components: {
    DateInput,
    DateTimeInput,
    DurationEdit,
    WarningDialog
  },
  props: {
    value: {
      type: Object,
      description: 'Risk Analysis'
    },
    timeZone: {
      type: String,
      description: 'Time zone to represent times in. Should be set to utc for standalone ARA.',
      default: 'local'
    },
    onlyDates: {
      type: Boolean,
      description: 'Drop the time component from the start and end dates',
      default: false
    },
    readonly: Boolean,
    nameRules: Array
  },
  data() {
    return {
      standardRules: Rules,
      localRules: {
        missionTimeInRange: value => {
          if (!this.$refs.missionTime || value === null || value == undefined || !Number.isInteger(Number(value)))
            return true

          let duration = Duration.fromObject(
            JSON.parse('{"' + this.$refs.missionTime.durationType + '": ' + value + '}')
          ).shiftTo('days')
          return (
            !duration.isValid ||
            (duration.days >= 1 && duration.days <= 36500) ||
            'Mission time must be a value between 1 day and 100 years'
          )
        }
      },
      ITUS_DATETIME_FORMAT: 'yyyy-MM-dd h:mm a',
      ARA_DATE_FORMAT: 'yyyy-MM-dd'
    }
  },
  computed: {
    ...mapState({
      scenarioInvalidationConfirmed: state => state.analyses?.scenarioInvalidationConfirmed,
      scenarios: state => state.analyses?.scenarios
    }),
    endDateHint() {
      if (this.value && this.value.endDate && DateTime.fromISO(this.value.endDate, { zone: 'utc' }) < DateTime.utc()) {
        return 'The analysis end date is in the past.'
      }
      return ''
    },
    combinedNameRules() {
      return this.nameRules
        ? [...this.nameRules, this.standardRules.required, this.standardRules.maxLength.bind(null, 50)]
        : [this.standardRules.required, this.standardRules.maxLength.bind(null, 50)]
    },
    endDateRules() {
      const displayFormat = this.onlyDates ? this.ARA_DATE_FORMAT : this.ITUS_DATETIME_FORMAT
      return [
        this.standardRules.dateAfter.bind(
          null,
          this.value.startDate,
          displayFormat,
          'Please choose a date after the start date.'
        )
      ]
    }
  },
  methods: {
    validate() {
      return this.$refs.form?.validate()
    },
    resetValidation() {
      return this.$refs.form?.resetValidation()
    },
    clearMissionTime() {
      let newData = new RiskAnalysisModel(this.value)
      newData.endDate = null
      this.$emit('input', newData)
    },
    clearEndDate() {
      let newData = new RiskAnalysisModel(this.value)
      newData.missionTime = null
      this.$emit('input', newData)
    },
    nameInput(newValue) {
      let newData = new RiskAnalysisModel(this.value)
      newData.analysisName = Convert.toString(newValue)
      this.$emit('input', newData)
    },
    descriptionInput(newValue) {
      let newData = new RiskAnalysisModel(this.value)
      newData.description = Convert.toString(newValue)
      this.$emit('input', newData)
    },
    async warnAboutInvalidatingScenarios() {
      if (!this.scenarios || this.scenarios.length === 0 || this.scenarios.filter(x => x.valid === true).length === 0)
        return false

      if (this.scenarioInvalidationConfirmed) return false

      let confirmChange = await this.$refs.warningDialog.show()

      if (confirmChange.suppress) this.$store.dispatch('analyses/setScenarioInvalidationConfirmed', true)

      return !confirmChange.result
    },
    async startDateInput(newValue) {
      if (await this.warnAboutInvalidatingScenarios()) {
        this.$refs.startDate.reset()
        return
      }

      let newData = new RiskAnalysisModel(this.value)
      if (!newValue && newData.endDate && newData.startDate && newData.missionTime) {
        newData.startDate = null
        newData.endDate = null
      } else if (!newValue || (!newData.endDate && !newData.missionTime)) {
        // nothing to do just set date
        newData.startDate = newValue
      } else if (!newData.missionTime && newData.endDate) {
        // calculated mission
        if (DateTime.fromISO(newData.endDate, { zone: 'utc' }) > DateTime.fromISO(newValue, { zone: 'utc' })) {
          newData.startDate = newValue
          newData.missionTime = Time.simpleDurationFromDates(newData.startDate, newData.endDate)
        }
      } else {
        // calculate end
        newData.startDate = newValue
        newData.endDate = DateTime.fromISO(newData.startDate, { zone: this.timeZone })
          .plus(Duration.fromISO(newData.missionTime))
          .set({ milliseconds: 0 })
          .toUTC()
          .toISO({ suppressMilliseconds: true })
      }

      this.$emit('input', newData)
    },
    async missionTimeInput(newValue) {
      if (await this.warnAboutInvalidatingScenarios()) {
        this.$refs.missionTime.decomposeValue()
        return
      }
      if (!this.$refs.missionTime.validate()) return

      let newData = new RiskAnalysisModel(this.value)
      if (!newValue && newData.endDate && newData.startDate && newData.missionTime) {
        newData.missionTime = null
        newData.endDate = null
      } else if (!newValue || (!newData.startDate && !newData.endDate)) newData.missionTime = newValue
      else if (!newData.startDate && newData.endDate) {
        newData.missionTime = newValue
      } else {
        newData.missionTime = newValue
        newData.endDate = DateTime.fromISO(newData.startDate, { zone: this.timeZone })
          .plus(Duration.fromISO(newData.missionTime))
          .set({ milliseconds: 0 })
          .toUTC()
          .toISO({ suppressMilliseconds: true })
      }

      this.$emit('input', newData)
    },
    async endDateInput(newValue) {
      if (await this.warnAboutInvalidatingScenarios()) {
        this.$refs.endDate.reset()
        return
      }

      let newData = new RiskAnalysisModel(this.value)

      if (!newData.startDate) {
        // nothing to do just set date
        newData.endDate = newValue
      } else if (!newValue) {
        newData.endDate = newValue
        newData.missionTime = null
      } else if (newValue && newData.startDate) {
        // calc mission time from start and end
        if (DateTime.fromISO(newValue, { zone: 'utc' }) > DateTime.fromISO(newData.startDate, { zone: 'utc' })) {
          newData.endDate = newValue
          newData.missionTime = Time.simpleDurationFromDates(newData.startDate, newData.endDate)
        }
      }

      this.$emit('input', newData)
    }
  },
  mounted() {
    if (this.value && !this.value.analysisName) {
      this.$refs.name.focus()
    }
  }
}
</script>
